import React, { Fragment, useEffect, useState } from "react";
import { Box, Button, Card, Grid2 as Grid, Stack, Typography } from "@mui/material";
import { TagManagmentHeader } from "../TagManagmentHeader";
import { GENERIC_COLUMN_PROPS, INITIAL_GET_PARAMS, SPECIAL_POPULAR_TAGS } from "../../../../enums/generalEnums";
import { TagManagementTable } from "../TagManagementTable";
import { getPinnedUsers, getUserList, pinUsers } from "../../../../config/apisauce";
import { GenericDialog } from "../../../../components/generic/GenericDialog";
import { useTheme } from "@emotion/react";
import { GenericAvatar } from "../../../../components/generic/GenericAvatar";
import { GenericDivider } from "../../../../components/generic/GenericDivider";
import { GenericFlag } from "../../../../components/generic/GenericFlag";
import { COUNTRIES_WITH_ISO } from "../../../../enums/countries";
import { GenericIconButton } from "../../../../components/generic/GenericIconButton";
import { PushPinSimple, UsersFour } from "@phosphor-icons/react";
import { ACTIVE } from "../../../../enums/statusEnums";
import { SearchField } from "../../../../components/generic/GenericTextFields";
import { addRemoveArray } from "../../../../helpers/arrayHelper";
import { GenericLinearLoader } from "../../../../components/generic/GenericLoader";
import useDebounce from "../../../../hooks/useDebounce";
import { GenericNoData } from "../../../../components/generic/GenericNoData";
import useNotiStack from "../../../../hooks/useNotiStack";
import LoadingButton from "@mui/lab/LoadingButton";
import { DeleteCategoryDialog } from "../DeleteCategoryDialog";
import useStore from "../../../../states/store";

export const TagList = () => {
  const theme = useTheme();
  const [, { openLoader, closeLoader }] = useStore();
  const { success } = useNotiStack();
  const [tagList, setTaglist] = useState();
  const [selectedTag, setSelectedTag] = useState(null);
  const [openPinCreatorDialog, setOpenPinCreatorDialog] = useState(false);
  const [creators, setCreators] = useState([]);
  const [filter, setFilter] = useState({ ...INITIAL_GET_PARAMS, status: ACTIVE });
  const [loadingUserList, setLoadingUserList] = useState(false);
  const [loadingSave, setLoadingSave] = useState(false);
  const [pinnedIds, setPinnedIds] = useState([]);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [pagination, setPagination] = useState({ pageSize: 5, page: 0 });
  const [openAddDialog, setOpenAddDialog] = useState(false);
  const isTagCollab = (selectedTag?.name === 'Collab');

  const fetchPinnedUsers = async () => {
    openLoader();
    const params = {
      ...INITIAL_GET_PARAMS,
      order_by: 'meta_order',
      per_page: pagination?.pageSize,
      page: pagination?.page + 1
    };
    const response = await getPinnedUsers(params);
    if (response.ok) {
      setTaglist(response?.data);
    }
    closeLoader();
  };

  const fetchUserList = async (params) => {
    setLoadingUserList(true);
    const response = await getUserList(params);
    if (response.ok) {
      setCreators(response?.data);
    }
    setLoadingUserList(false);
  };

  const handleSearch = useDebounce((searchParams) => {
    fetchUserList({ ...filter, search: searchParams });
  }, 500);

  const handleOpenDeleteDialog = () => {
    setOpenDeleteDialog(true);
    setOpenPinCreatorDialog(false);
  };

  const handleScroll = (event) => {
    const bottom = event.target.scrollHeight - event.target.scrollTop === event.target.clientHeight;
    if (bottom && !loadingUserList) {
      setFilter((prev) => ({
        ...prev,
        per_page: creators?.per_page + 20
      }));
    }
  };

  const handlePinUpin = (isAdd, id) => {
    setPinnedIds((prev) => addRemoveArray(prev, isAdd, id));
  };

  const handleSave = async () => {
    setLoadingSave(true);
    const response = await pinUsers({ id: selectedTag?.id, user_ids: pinnedIds });
    if (response.ok) {
      fetchPinnedUsers();
      setOpenPinCreatorDialog(false);
      success('Successfully saved!');
      setSelectedTag(null);
    }
    setLoadingSave(false);
  };

  const handleOpenPinCreatorDialog = (values) => {
    setOpenPinCreatorDialog(true);
    setSelectedTag(values);
    setPinnedIds(values?.user_ids);
    const filterValues = (values?.name === 'Collab') ? {}
      : SPECIAL_POPULAR_TAGS?.TAGS?.includes(values?.name)
        ? SPECIAL_POPULAR_TAGS?.[values?.name]
        : { category_values: values?.name };
    setFilter(prev => ({ ...prev, filters: filterValues }));
  };

  const handleClosePinCreatorDialog = () => {
    setOpenPinCreatorDialog(false);
    setSelectedTag(null);
  };

  const handleCloseDeleteDialog = () => {
    setOpenDeleteDialog(false);
    setSelectedTag(null);
  };

  const action = {
    field: 'action',
    headerName: 'Action',
    flex: 0.7,
    renderCell: ({ row }) => {
      return (
        <Button variant="contained" fullWidth onClick={() => handleOpenPinCreatorDialog(row)}>Manage</Button>
      );
    },
    ...GENERIC_COLUMN_PROPS,
  };

  useEffect(() => {
    if (openPinCreatorDialog) fetchUserList({ ...filter });
  }, [openPinCreatorDialog, filter]);

  useEffect(() => {
    fetchPinnedUsers();
  }, [pagination]);

  return (
    <Fragment>
      <Card>
        <TagManagmentHeader
          title={'Tags'}
          subTitle={'View and manage all your tags.'}
          selectedCategory={selectedTag}
          alterList={setTaglist}
          openAddDialog={openAddDialog}
          setOpenAddDialog={setOpenAddDialog}
          setSelectedCategory={setSelectedTag}
        />
        <TagManagementTable
          requestData={tagList}
          customActions={action}
          setPagination={setPagination}
          handleOpenDialog={(params) => handleOpenPinCreatorDialog(params)}
        />
      </Card>
      <GenericDialog
        title={'Pin Creators'}
        width={'903px'}
        openDialog={openPinCreatorDialog}
        handleClose={() => handleClosePinCreatorDialog()}
      >
        <Typography variant="subtitle1" mb={2}>Select profiles to be pinned in the
          <span style={{ fontWeight: 700, color: theme.palette.primary.main }}> {selectedTag?.name} Tag</span>.
        </Typography>
        <SearchField
          width={'100%'}
          placeholder={'Search Creators...'}
          handleChange={(value) => handleSearch(value)}
        />
        {loadingUserList && <GenericLinearLoader margin={'15px 0 0 0'} />}
        <Box
          {...(creators?.per_page < creators?.total && { onScroll: handleScroll })}
          sx={{
            overflowX: 'auto',
            backgroundColor: '#FFF',
            height: '40vh',
            borderRadius: '10px',
            padding: '16px',
            mt: 1
          }}
        >
          {
            creators?.data?.length > 0 ? creators?.data?.map((creator, index) => {
              const isPinned = pinnedIds?.includes(creator?.id);
              return (
                <Fragment key={`creator-key-${index}`}>
                  <Grid container justifyContent={'space-between'}>
                    <Grid item>
                      <Stack direction={'row'} gap={1.5} alignItems={'center'}>
                        <GenericAvatar size={'32px'} location={creator?.details?.profile_picture?.location} />
                        <Box>
                          <Stack gap={1} direction={'row'} alignItems={'center'}>
                            <Typography fontWeight={700}>{creator?.name}</Typography>
                            <GenericFlag code={COUNTRIES_WITH_ISO?.[creator?.details?.country]} />
                          </Stack>
                          <Typography color="text.semiLight">{creator?.details?.profession}</Typography>
                        </Box>
                      </Stack>
                    </Grid>
                    <Grid item>
                      <GenericIconButton
                        onClick={() => handlePinUpin(!isPinned, creator?.id)}
                        color={'primary.main'}
                        icon={<PushPinSimple size={24} weight={isPinned ? 'fill' : 'regular'} />}
                      />
                    </Grid>
                  </Grid>
                  <GenericDivider />
                </Fragment>
              );
            }) : <GenericNoData icon={<UsersFour size={40} />} height={'36vh'} />
          }
        </Box>
        <Grid container justifyContent={{ sm: 'space-between' }} mt={2} spacing={1}>
          <Grid item size={{ xs: 12, sm: 6 }}>
            {!isTagCollab && <Button variant="outlined" color="error" sx={{ width: { xs: '100%', sm: '150px' } }} onClick={() => handleOpenDeleteDialog()}>Delete Tag</Button>}
          </Grid>
          <Grid item size={{ xs: 12, sm: 6 }}>
            <Stack direction={{ xs: 'column', sm: 'row' }} gap={1}>
              <Button variant="outlined" sx={{ width: { xs: '100%', sm: '250px' } }} onClick={() => handleClosePinCreatorDialog()}>Cancel</Button>
              <LoadingButton
                onClick={() => handleSave()}
                loading={loadingSave}
                variant="contained"
                disabled={loadingSave}
                sx={{ width: { xs: '100%', sm: '250px' } }}
              >
                Save
              </LoadingButton>
            </Stack>
          </Grid>
        </Grid>
      </GenericDialog>
      <DeleteCategoryDialog
        selectedTag={selectedTag}
        openDeleteDialog={openDeleteDialog}
        handleClose={() => handleCloseDeleteDialog()}
        alterList={setTaglist}
      />
    </Fragment >
  );
};